/// <amd-module name="Analytics/Boards/BoardsQueries/ProcessesQuery" />
import { AnalyticsCacheableQueryBase, CommonFeatureCommandName } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { forceProjectScoping } from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
/** Provides an association between known backlog categories and work item types contained within. */
export interface ProcessesEntry {
    BacklogCategoryReferenceName: string;
    WorkItemType: string;

    // Used for recognition of work items which are used for "bugs", for special treatment in conjunction with Requirements backlogs.
    IsBugType: boolean;

    // Used for presentation of the "plural" name of a backlog
    BacklogName: string;

    // Used for Delete status of WorkItemType 
    IsDeleted:boolean
}

/**
 * Get the list of the "Process" Entities associated for the specified Project or Team.
 * At the Team Level, bugs are auto-included based on process config of the team.
 */
export class ProcessesQuery extends AnalyticsCacheableQueryBase<ProcessesEntry[]> {
    private static generateQueryOptions(pageContext: IVssPageContext, projectId: string, teamId?: string): ODataQueryOptions {
        const teamExpression = teamId != null ? `TeamSK eq ${teamId} and ` : "";
        const filter = `filter(${teamExpression} ProjectSK eq ${projectId})`;
        // Adding isDeleted Prop based on Availability flag
        const isDeleteExpression = isFeatureFlagEnabled(pageContext, "WebAccess.Process.WorkItemTypeIsDeletedOnProcess", false) ? `, IsDeleted`:"";
        return forceProjectScoping(pageContext, [projectId], {
            entityType: "Processes",
            oDataVersion: latestODataVersion,
            $apply: `${filter}/groupby((BacklogCategoryReferenceName, WorkItemType, IsBugType, BacklogName${isDeleteExpression}))`
        });
    }

    public static onProject(pageContext: IVssPageContext, projectId: string): ProcessesQuery {
        return new ProcessesQuery(ProcessesQuery.generateQueryOptions(pageContext, projectId));
    }

    public static onTeam(pageContext: IVssPageContext, projectId: string, teamId: string) {
        if (teamId == null || teamId.trim().length === 0) {
            throw new Error("Team ID must be provided to use this method. Use onProject instead if querying for processes at the project level.");
        }

        return new ProcessesQuery(ProcessesQuery.generateQueryOptions(pageContext, projectId, teamId));
    }
    private queryName?: string;

    public constructor(options: ODataQueryOptions, queryName?: string) {
        super(CommonFeatureCommandName, options);
        this.queryName = queryName;
    }

    public getQueryName(): string {
        return this.queryName || "ProcessesQuery";
    }
}
